// Detail view of one hotel post (Instagram-Fotos, Instagram-Reels, LinkedIn-Beiträge): image or reel player,
// text, hashtags, Rubrik, date;
// edit text and delete while the server says so (editability), "Freigeben" leads to the weekly approval
// (PLAN.md §7.3, Mike B4: approval stays per week until 16.10.).
import { useEffect, useState, type ReactNode } from "react"
import { Link } from "wouter"
import { format } from "date-fns"
import { de } from "date-fns/locale"
import { Hash, Pencil, Trash2, CheckCircle } from "lucide-react"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { MEDIA_FORMAT_LABELS, effectiveMediaFormat } from "@shared/pillarFormats"
import { EDIT_HINT, REEL_EDIT_HINT, LOCK_HINT, useHotelPostMutations, type HotelPost } from "./hotelPosts"
import { PostSlides, hasSlides } from "@/components/PostSlides"

export function postDateLabel(post: HotelPost): string {
  const when = post.scheduledFor ?? post.createdAt
  return when ? format(new Date(when), "EEEE, dd. MMMM yyyy", { locale: de }) : ""
}

export function HotelPostDialog({
  post, open, onOpenChange, imageAspectClass, media,
}: {
  post: HotelPost | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Tailwind aspect class of the image frame (4:5 → aspect-[4/5]); omitted for text posts. */
  imageAspectClass?: string
  /** Replaces the image frame (Instagram-Reels: the 9:16 player). */
  media?: ReactNode
}) {
  const [editing, setEditing] = useState(false)
  const [title, setTitle] = useState("")
  const [content, setContent] = useState("")
  const [confirmDelete, setConfirmDelete] = useState(false)
  const { edit, remove } = useHotelPostMutations(() => {
    setEditing(false)
    setConfirmDelete(false)
    onOpenChange(false)
  })

  useEffect(() => {
    setEditing(false)
    setTitle(post?.title ?? "")
    setContent(post?.postContent ?? "")
  }, [post?.id, post?.version])

  if (!post) return null
  const editability = post.editability ?? null
  const canEdit = !!editability?.canEdit
  const canDelete = !!editability?.canDelete
  const inReview = editability?.reason === "plan_review"
  const lockHint = editability && !canEdit && !canDelete ? LOCK_HINT[editability.reason] : undefined
  const hashtags = Array.isArray(post.hashtags) ? (post.hashtags as string[]) : []

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto flex flex-col [&>*]:shrink-0" data-testid="hotel-post-dialog">
          <DialogHeader>
            <DialogTitle>{post.title || "Ohne Titel"}</DialogTitle>
            <DialogDescription>
              {MEDIA_FORMAT_LABELS[effectiveMediaFormat(post)]} · {postDateLabel(post)}
            </DialogDescription>
          </DialogHeader>

          {media}

          {!media && hasSlides(post) && imageAspectClass && (
            <PostSlides
              urls={post.imageUrls}
              alt={post.title || "Beitragsbild"}
              className={`w-full max-w-sm mx-auto ${imageAspectClass} bg-muted rounded-lg overflow-hidden`}
              imgClassName="w-full h-full object-cover"
              testId="hotel-post-slides"
            />
          )}
          {!media && !hasSlides(post) && post.imageUrl && imageAspectClass && (
            <div className={`relative w-full max-w-sm mx-auto ${imageAspectClass} bg-muted rounded-lg overflow-hidden`}>
              <img src={post.imageUrl} alt={post.title || "Beitragsbild"} className="w-full h-full object-cover" />
            </div>
          )}

          {editing ? (
            <div className="space-y-3">
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titel" data-testid="input-hotel-post-title" />
              <Textarea value={content} onChange={(e) => setContent(e.target.value)} rows={10} data-testid="input-hotel-post-content" />
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm whitespace-pre-wrap">{post.postContent}</p>
              {hashtags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {hashtags.map((tag, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      <Hash className="h-2 w-2 mr-1" />
                      {String(tag).replace("#", "")}
                    </Badge>
                  ))}
                </div>
              )}
              {post.pillarName && (
                <p className="text-xs text-muted-foreground">Rubrik: {post.pillarName}</p>
              )}
            </div>
          )}

          {(canEdit || canDelete) && (
            <p className="text-xs text-muted-foreground">{effectiveMediaFormat(post) === "reel" ? REEL_EDIT_HINT : EDIT_HINT}</p>
          )}
          {lockHint && <p className="text-xs text-muted-foreground" data-testid="hotel-post-lock">{lockHint}</p>}

          <DialogFooter className="gap-2 sm:justify-between">
            <div className="flex gap-2">
              {canDelete && !editing && (
                <Button variant="ghost" className="text-red-600" onClick={() => setConfirmDelete(true)} data-testid="button-hotel-post-delete">
                  <Trash2 className="h-4 w-4 mr-1" /> Löschen
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {editing ? (
                <>
                  <Button variant="outline" onClick={() => setEditing(false)}>Abbrechen</Button>
                  <Button
                    onClick={() => edit.mutate({ id: post.id, title, postContent: content, version: post.version })}
                    disabled={edit.isPending || !content.trim()}
                    data-testid="button-hotel-post-save"
                  >
                    {edit.isPending ? "Speichert…" : "Speichern"}
                  </Button>
                </>
              ) : (
                <>
                  {canEdit && (
                    <Button variant="outline" onClick={() => setEditing(true)} data-testid="button-hotel-post-edit">
                      <Pencil className="h-4 w-4 mr-1" /> Text bearbeiten
                    </Button>
                  )}
                  {inReview && (
                    <Button asChild data-testid="button-hotel-post-approve">
                      <Link href="/wochenplan"><CheckCircle className="h-4 w-4 mr-1" /> Freigeben</Link>
                    </Button>
                  )}
                </>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Beitrag löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Der Beitrag wird nicht veröffentlicht und auch nicht erneut vorgeschlagen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => remove.mutate({ id: post.id, version: post.version })}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-hotel-post-delete-confirm"
            >
              {remove.isPending ? "Löscht…" : "Löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
